/*
  HL SHOPABILITY P1 — one cart API for the whole page.

  WHY THIS EXISTS
  ---------------
  A plain page load on this store issues five to seven calls to the cart API
  before the shopper has touched anything. Most of them are one app polling:
  the Analyzely GA4 embed runs `setInterval(function(){ fetch('/cart.js') },
  3000)`, so every open tab asks Shopify for the cart twenty times a minute
  for as long as it stays open. Selleasy and the DECO badge extension each
  add one more on load.

  Shopify rate-limits the cart endpoints. Past that limit it stops answering
  with JSON and answers with an HTML "Verifying your connection" page under
  HTTP 429 — which is exactly what `.then(r => r.json())` reports as a failed
  fetch. That is the shape of the 22,910 cart-API errors in the logs, and it
  is reproducible: driving the endpoints at the rate the page already drives
  them puts this store into the challenge within a minute.

  WHAT IT DOES
  ------------
  Wraps window.fetch and XMLHttpRequest before any app script runs, so every
  cart read on the page — ours and theirs — goes through one door:

    * concurrent reads share a single in-flight request;
    * a read is served from a short cache instead of hitting the network, so
      a 3-second poll costs nothing;
    * the cache is dropped the moment anything writes to the cart, so a poll
      still sees an add or a remove immediately;
    * a read that fails, or that comes back as the HTML challenge page, is
      retried once with backoff before the caller is told anything went
      wrong — so a single throttled response is no longer a dead drawer.

  It changes no call site. Anything that already calls /cart.js keeps working
  and simply gets answered faster and more often from memory.
*/
(function () {
  'use strict';

  if (window.HLCart) return;

  var STALE_MS   = 15000;  /* a passive read may be this old before we refetch */
  var RETRY_MS   = 600;    /* backoff before the single retry                  */
  var FAIL_MS    = 3000;   /* after a failure, shared reads back off this long */
  var THROTTLE_MS = 8000;  /* after a 429, back off a little longer            */
  /* q859: a back-off NEVER refuses a cart read. While it runs, a caller with no
     cached cart gets Shopify's own native response to its own request instead
     of "cart.js unavailable (cooling down)". Measured 2 Nov: that rejection
     fired ~9.2k times since 1 Oct, almost all for Meta's ad crawler (Shopify
     answers it 401), but a shopper must never be the one it lands on. */
  function cartRoot() {
    try {
      var r = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
      if (typeof r === 'string' && r.charAt(0) === '/') return r.charAt(r.length - 1) === '/' ? r : r + '/';
    } catch (e) {}
    return '/';
  }
  var CART_READ  = /\/cart\.js(\?|$)/;
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js|$|\?)/;

  var origFetch = window.fetch && window.fetch.bind(window);
  if (!origFetch) return;

  var cachedText = null;
  var cachedAt   = 0;

  /* ---- header badge -----------------------------------------------------
     Every cart the page learns about - a read, or the body of a write -
     passes through setCached(). That is the one place that knows the live
     item count, so the header badge is painted from here rather than from
     each caller: an add from the PDP, quick-add, Selleasy or the drawer's
     own +/- all land in the same place. The badge elements are rendered by
     Liquid on every page (hidden at zero), so there is always something to
     paint. Also fires `hl:cart` on document for anything else that wants
     the parsed cart. */
  var lastCount = null;
  function paintBadge(cart) {
    if (!cart || typeof cart.item_count !== 'number') return;
    if (cart.item_count === lastCount) return;
    lastCount = cart.item_count;
    var els = document.querySelectorAll('.cart-link__count, .hl-bag-count');
    for (var i = 0; i < els.length; i++) {
      els[i].textContent = String(cart.item_count);
      if (cart.item_count > 0) els[i].removeAttribute('hidden'); else els[i].setAttribute('hidden', '');
    }
    try { document.dispatchEvent(new CustomEvent('hl:cart', { detail: cart })); } catch (e) {}
  }
  function setCached(text) {
    cachedText = text;
    cachedAt = Date.now();
    failedAt = 0;
    try { paintBadge(JSON.parse(text)); } catch (e) {}
  }
  var inflight   = null;
  var failedAt   = 0;
  /* q859: every write bumps writeSeq. A write's response (or a read that was
     already in the air) may only become the cached cart if no LATER write
     has started since - otherwise a slow add response landing after a
     change/remove would put the old cart back (measured on dev, 2 Nov:
     add then remove under /fr/ left item_count at 1). */
  var writeSeq   = 0;
  var failCooldown = FAIL_MS;

  /* ---- request shape helpers -------------------------------------------
     Callers pass a string, a URL, or a Request object. The Request-object
     branch matters: apps on this store (Globo, Selleasy) use it, and a
     typeof-string check alone misses every one of them. */
  function describe(args) {
    var input = args[0];
    var init = args[1] || {};
    var url = '';
    var method = (init.method || 'GET').toUpperCase();
    try {
      if (typeof input === 'string') {
        url = input;
      } else if (input && typeof input === 'object') {
        if (typeof input.url === 'string') url = input.url;
        else url = String(input);
        if (input.method) method = String(input.method).toUpperCase();
      }
    } catch (e) {}
    return { url: url, method: method };
  }

  function looksLikeJson(text) {
    var t = (text || '').replace(/^\uFEFF/, '').trim();
    return t.charAt(0) === '{' || t.charAt(0) === '[';
  }

  function jsonResponse(text) {
    return new Response(text, {
      status: 200,
      headers: { 'Content-Type': 'application/json; charset=utf-8' },
    });
  }

  /* ---- the one real read ------------------------------------------------ */
  function fetchCartText(attempt) {
    return origFetch(cartRoot() + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.text().then(function (text) {
          /* A throttled or challenged response is HTML, not JSON. Treat it
             as a failure so the caller is never handed an HTML string to
             JSON.parse - which is how this shows up in the logs today. */
          if (!r.ok || !looksLikeJson(text)) {
            var e = new Error('cart.js ' + r.status);
            e.status = r.status;
            /* 429 means "you are asking too often". Retrying immediately is
               the one response guaranteed to make it worse, so a throttle is
               never retried and buys a longer cooldown than a dropped
               connection does. */
            e.throttled = r.status === 429 || r.status === 430;
            /* 401/403 is bot protection or a locale route refusing us:
               retrying cannot help, so it fails fast to the native path. */
            e.denied = r.status === 401 || r.status === 403;
            throw e;
          }
          return text;
        });
      })
      .catch(function (err) {
        if (attempt || (err && (err.throttled || err.denied))) throw err;
        return new Promise(function (resolve, reject) {
          setTimeout(function () { fetchCartText(1).then(resolve, reject); }, RETRY_MS);
        });
      });
  }

  function readCart(force) {
    var fresh = cachedText !== null && (Date.now() - cachedAt) < STALE_MS;
    if (!force && fresh) return Promise.resolve(cachedText);
    if (inflight) return inflight;
    /* NEGATIVE CACHING. The failure this whole file exists to contain is a
       rate limit, and the worst thing to do to a rate limit is to answer it
       with more requests. Without this, a cart API that is refusing us turns
       a 3-second poll into a 3-second poll PLUS a retry each time — twice
       the traffic, at exactly the moment the store is asking for less. So a
       failure closes the door for FAIL_MS: callers get the last known cart
       if we have one, and a rejection if we do not, with nothing put on the
       wire either way. */
    if ((Date.now() - failedAt) < failCooldown) {
      if (cachedText !== null) return Promise.resolve(cachedText);
      /* No cached cart: never refuse. The caller falls through to native. */
      var e = new Error('cart.js backing off'); e.backoff = true;
      return Promise.reject(e);
    }
    var seqAtStart = writeSeq;
    inflight = fetchCartText(0)
      .then(function (text) {
        if (seqAtStart === writeSeq) setCached(text);
        inflight = null;
        return text;
      })
      .catch(function (err) {
        failedAt = Date.now();
        failCooldown = (err && err.throttled) ? THROTTLE_MS : FAIL_MS;
        inflight = null;
        throw err;
      });
    return inflight;
  }

  function invalidate() {
    cachedText = null;
    cachedAt = 0;
    /* A write is a fresh reason to ask, so it also lifts the cooldown. */
    failedAt = 0;
  }

  /* ---- public surface --------------------------------------------------- */
  window.HLCart = {
    /* Resolves with the parsed cart. `force` skips the cache but still
       shares an in-flight request. Rejects only after the retry failed. */
    get: function (force) {
      return readCart(!!force).then(function (text) { return JSON.parse(text); }, function () {
        /* Native fallback: one plain request, Shopify's own answer. */
        return origFetch(cartRoot() + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
          .then(function (r) { if (!r.ok) throw new Error('cart.js ' + r.status); return r.json(); })
          .then(function (cart) { try { setCached(JSON.stringify(cart)); } catch (e) {} return cart; });
      });
    },
    /* Last cart we successfully read, or null. Lets a caller paint
       something truthful while a refresh is in the air. */
    peek: function () {
      try { return cachedText ? JSON.parse(cachedText) : null; } catch (e) { return null; }
    },
    invalidate: invalidate,
  };

  /* ---- fetch ------------------------------------------------------------ */
  window.fetch = function () {
    var info = describe(arguments);
    if (CART_WRITE.test(info.url)) {
      invalidate();
      var mySeq = ++writeSeq;
      return origFetch.apply(this, arguments).then(function (r) {
        /* Every cart write answers with the WHOLE updated cart. Reading it
           from the write's own response is free, and it means the next
           reader is served from memory instead of the page paying for a
           fresh /cart.js immediately after every change - which was two of
           the three reads a first page load used to cost, because an app
           writes a session attribute to the cart on load. */
        try {
          r.clone().text().then(function (text) {
            if (mySeq === writeSeq && looksLikeJson(text) && text.indexOf('"item_count"') > -1) {
              setCached(text);
            }
          }, function () {});
        } catch (e) { invalidate(); }
        return r;
      });
    }
    if (info.method === 'GET' && CART_READ.test(info.url)) {
      var self = this, callArgs = arguments;
      /* Shared, cached read first; on any failure the caller's own request
         goes to Shopify untouched, exactly as if this file were not here. */
      return readCart(false).then(jsonResponse, function () {
        return origFetch.apply(self, callArgs);
      });
    }
    return origFetch.apply(this, arguments);
  };

  /* ---- XMLHttpRequest ---------------------------------------------------
     Older app bundles still use XHR. We only need the write half here: an
     XHR read is rare and passing it through costs one request, whereas
     missing an XHR write would leave the cache stale after a real change. */
  var origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    try {
      if (CART_WRITE.test(String(url))) {
        var xSeq = ++writeSeq;
        this.addEventListener('loadend', function () {
          invalidate();
          /* An app adding by XHR answers with the whole cart as well. */
          try {
            var t = this.responseText;
            if (xSeq === writeSeq && this.status === 200 && looksLikeJson(t) && t.indexOf('"item_count"') > -1) setCached(t);
          } catch (e) {}
        });
        invalidate();
      }
    } catch (e) {}
    return origOpen.apply(this, arguments);
  };

  /* A cart can change in another tab, or while this one was backgrounded. */
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) invalidate();
  });
  /* Only a BFCACHE restore is a reason to distrust the cache. The plain
     pageshow that follows every ordinary load fires just after the first
     read, so invalidating on it threw away a cart we had only just fetched
     and made the page pay for a second one. */
  window.addEventListener('pageshow', function (e) { if (e && e.persisted) invalidate(); });
}());
